import { Box, Grid2, Tab } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import PersonIcon from "@mui/icons-material/Person";
import style from "./Subject.module.css";
import {
  getPatientDiaryDetailsService,
  getPatientVisitDataService,
  getSubjectDetailsByIdService,
} from "../../../services/subjectService";
import {
  formatDateWithoutTime,
  validDateRegex,
} from "../../../utilities/helper";
import SubjectVisitManagement from "./SubjectVisitManagement/SubjectVisitManagement";
import SmallLoader from "../../../components/SmallLoader";
import CareGiverManagement from "./CareGiverManagement/CareGiverManagement";
import DrugKitsManagement from "./DrugKitsManagement/DrugKitsManagement";

const UpdateSubject = () => {
  const id = useLocation()?.state?.id;
  const siteId = useLocation()?.state?.siteId;

  const [load, setLoad] = useState(false);

  const [canUpdateSubject, setCanUpdateSubject] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [tabValue, setTabValue] = useState("subjectManagement");
  const [subjectDetails, setSubjectDetails] = useState({
    subjectName: "",
    status: "",
    attributes: [],
  });

  const [visitData, setVisitData] = useState([]);
  const [checkElegibility, setCheckEligibility] = useState({
    eligbleForUnblind: false,
    eligbleForOverrideRandomize: false,
  });

  const navigate = useNavigate();

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const cultureCode = localStorage.getItem("currentUserCultureCode");
  const userId = localStorage.getItem("roleId");

  const { attributes, subjectName, status } = subjectDetails;
  const { eligbleForOverrideRandomize, eligbleForUnblind } = checkElegibility;

  const access_token = localStorage.getItem("access_token");
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (id) {
      getSubjectDetails(id);
    }
  }, [id]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanEditPatient",
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateSubject(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateSubject(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (canUpdateSubject) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateSubject]);

  useEffect(() => {
    if (tabValue === "visits" && id) {
      getPatientVisitData(id);
    }
  }, [tabValue, id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getSubjectDetails = async (id) => {
    try {
      setLoad(true);
      const res = await getSubjectDetailsByIdService(
        access_token,
        id,
        cultureCode,
        [],
      );
      if (res?.status == 200 && res?.data?.status === "OK") {
        const patientData = res?.data?.result?.patientData;
        setSubjectDetails({
          subjectName: patientData?.patientNumber || "",
          status: patientData?.patientStatus || "",
          attributes: patientData?.attributes || [],
        });
        setCheckEligibility({
          eligbleForOverrideRandomize:
            res?.data?.result?.eligibleForRandomizationOverride,
          eligbleForUnblind: res?.data?.result?.eligibleForUnblinding,
        });
      }
    } catch (err) {
      console.log("Error fetching subject details:", err);
    } finally {
      setLoad(false);
    }
  };

  const getPatientVisitData = async (patientId) => {
    try {
      setLoad(true);
      const res = await getPatientVisitDataService(access_token, patientId);
      if (res?.data?.status === "200") {
        setVisitData(res?.data?.result || []);
      }
    } catch (err) {
      console.log("Error:", err?.message);
      if (err?.status === 401) {
        navigate("/unAuhthorized");
      }
    } finally {
      setLoad(false);
    }
  };

  return (
    <div className="content-body">
      <p className="bread-crumbs-link-container">
        <button className="bread-crumbs-btn" onClick={(e) => navigate("/")}>
          Home
        </button>{" "}
        |{" "}
        <button
          className="bread-crumbs-btn"
          onClick={(e) => navigate("/subject")}
        >
          Subject Management
        </button>{" "}
        |{" "}
        <button className="bread-crumbs-btn" to="/update-subject">
          {subjectName}
        </button>
        - <b style={{ color: "gray" }}>{localStorage?.getItem("studyName")}</b>
      </p>
      {/* Tab Section */}
      <Box sx={{ width: "100%", mt: 2 }}>
        <TabContext value={tabValue}>
          {/* TABS HEADER */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2, // tab must stay above panel
            }}
          >
            <TabList
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                minHeight: "48px",

                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  minHeight: "48px",
                  border: "1px solid transparent",
                  borderBottom: "none",
                  borderRadius: "8px 8px 0 0",
                  backgroundColor: "transparent",
                  color: "text.secondary",
                  position: "relative",
                  zIndex: 1,
                },

                "& .Mui-selected": {
                  backgroundColor: "#FFFFFF",
                  color: "primary.main",
                  fontWeight: 600,
                  border: "1px solid #d0d7e2",
                  borderBottom: "none",
                  zIndex: 3,
                },
              }}
            >
              <Tab label="Subject Management" value="subjectManagement" />
              {/* <Tab label="Caregiver Management" value="careGiverManagement" /> */}
              <Tab label="Visits" value="visits" />
              <Tab label="Drug Kits" value="drugKits" />
            </TabList>
          </Box>

          {/* CONNECTED PANEL */}
          <Box
            sx={{
              border: "1px solid #d0d7e2",
              borderRadius: "0 8px 8px 8px",
              backgroundColor: "#ffffff",
              p: 3,

              marginTop: "-1px",

              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              position: "relative",
              zIndex: 1,
            }}
          >
            {load ? (
              <SmallLoader />
            ) : (
              <>
                {/* SUBJECT MANAGEMENT */}
                <TabPanel value="subjectManagement" sx={{ p: 0 }}>
                  <div
                    className="subject-name-container"
                    style={{ padding: "0px", paddingBottom: "20px" }}
                  >
                    <PersonIcon sx={{ fontSize: "50px", color: "black" }} />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>{subjectName}</span>
                      <span className="subject-id">{status}</span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "30px",
                      // justifyContent: "space-between",
                      justifyContent: "center",
                      // padding: "0 30px",
                    }}
                  >
                    <button
                      className="create-btn-outlined medium hover"
                      onClick={() =>
                        navigate("/subject-data-correction", { state: { id } })
                      }
                    >
                      Data Correction
                    </button>

                    {/* {eligbleForUnblind && ( */}
                    <button
                      className="create-btn-outlined medium hover"
                      onClick={() =>
                        navigate("/unblind-subject", { state: { id } })
                      }
                    >
                      Unblind Subject
                    </button>
                    {/* )} */}

                    {/* {!eligbleForOverrideRandomize && ( */}
                    <button
                      className="create-btn-outlined medium hover"
                      onClick={() =>
                        navigate("/override-randomization-subject", {
                          state: { id: id, siteId: siteId },
                        })
                      }
                    >
                      Override Randomization
                    </button>
                    {/* )} */}

                    <button
                      className="create-btn-outlined medium hover"
                      onClick={() =>
                        navigate("/subject-randomization-treatment", {
                          state: { id },
                        })
                      }
                    >
                      Register Randomization or Treatment error
                    </button>

                    <button
                      className="create-btn-outlined medium hover"
                      onClick={() =>
                        navigate("/subject-test", { state: { id } })
                      }
                    >
                      Test
                    </button>
                  </div>

                  {attributes?.length > 0 && (
                    <div className={style?.subjectDetailMainContainer}>
                      {attributes.map((item) => (
                        <div
                          key={item?.keyName}
                          className={style?.subjectDetailOuterContainer}
                        >
                          <span className={style?.subjectDetailName}>
                            {item?.label}
                          </span>
                          <span className={style?.subjectDetailValue}>
                            {validDateRegex.test(item?.value)
                              ? formatDateWithoutTime(item?.value)
                              : item?.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabPanel>

                {/* CARE GIVER MANAGAMENR */}
                {/* <TabPanel value="careGiverManagement" sx={{ p: 0 }}>
                  <CareGiverManagement />
                </TabPanel> */}

                {/* VISITS */}
                <TabPanel value="visits" sx={{ p: 0 }}>
                  <Grid2 container sx={{ mt: -1 }}>
                    <Grid2 item md={12}>
                      <p className="page-heading">Subject Visits</p>
                    </Grid2>
                  </Grid2>

                  {visitData &&
                  visitData.sort((a, b) => a.visitOrder - b.visitOrder)
                    ?.length > 0 ? (
                    visitData.map((visitItem) => (
                      <SubjectVisitManagement
                        key={visitItem?.id}
                        data={visitItem}
                      />
                    ))
                  ) : (
                    <p>No visits available for this subject.</p>
                  )}
                </TabPanel>

                {/* CARE GIVER MANAGAMENR */}
                <TabPanel value="drugKits" sx={{ p: 0 }}>
                  <DrugKitsManagement
                    culturalCode={cultureCode}
                    patientId={id}
                  />
                </TabPanel>
              </>
            )}
          </Box>
        </TabContext>
      </Box>
    </div>
  );
};

export default UpdateSubject;
